import { Tabs, TabsList, TabsTrigger } from "@devhop/ui";
import { LayoutGridIcon, ListIcon } from "lucide-react";
import { useState } from "react";
import type { AssignmentDTO, EventShowDTO } from "../../domain/contracts";
import {
  EventAssignmentsList,
  EventTeamAssignmentFilters,
  type TeamAssignmentFilter,
} from "./EventAssignmentsList";
import {
  ShowAssignmentMatrix,
} from "./ShowAssignmentMatrix";

export type TeamPanelTab = "list" | "matrix";

type EventTeamPanelTabsProps = {
  eventId: string;
  assignments: AssignmentDTO[];
  eventShows: EventShowDTO[];
  canUpdate: boolean;
  canDelete: boolean;
  deletePending: boolean;
  matrixSubmitting: boolean;
  onBulkUpdate: (
    updates: Array<{ assignmentId: string; eventShowIds: string[] }>,
  ) => Promise<void>;
  onAddShowToAll?: (eventShowId: string) => Promise<void>;
  getInitials: (name: string) => string;
  onViewPerformer: (performerId: string) => void;
  onEdit: (assignment: AssignmentDTO) => void;
  onDelete: (assignmentId: string) => void | Promise<void>;
};

export function EventTeamPanelTabs({
  eventId,
  assignments,
  eventShows,
  canUpdate,
  canDelete,
  deletePending,
  matrixSubmitting,
  onBulkUpdate,
  onAddShowToAll,
  getInitials,
  onViewPerformer,
  onEdit,
  onDelete,
}: EventTeamPanelTabsProps) {
  const hasMultiShow = eventShows.length >= 2;
  const [activeTab, setActiveTab] = useState<TeamPanelTab>("list");
  const [teamFilter, setTeamFilter] = useState<TeamAssignmentFilter>("all");

  const listContent = (
    <>
      <EventTeamAssignmentFilters
        assignments={assignments}
        value={teamFilter}
        onChange={setTeamFilter}
      />
      <EventAssignmentsList
        assignments={assignments}
        eventShows={eventShows}
        totalEventShowCount={eventShows.length}
        filter={teamFilter}
        canUpdate={canUpdate}
        canDelete={canDelete}
        deletePending={deletePending}
        getInitials={getInitials}
        onViewPerformer={onViewPerformer}
        onEdit={onEdit}
        onDelete={onDelete}
      />
    </>
  );

  const matrixContent = (
    <div className="space-y-4 px-5 py-4">
      <ShowAssignmentMatrix
        eventId={eventId}
        assignments={assignments}
        eventShows={eventShows}
        canManage={canUpdate}
        submitting={matrixSubmitting}
        onBulkUpdate={onBulkUpdate}
        onAddShowToAll={onAddShowToAll}
      />
    </div>
  );

  if (!hasMultiShow) {
    return listContent;
  }

  return (
    <>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-5 py-3">
        <Tabs
          value={activeTab}
          onValueChange={(value) => setActiveTab(value as TeamPanelTab)}
        >
          <TabsList className="h-9">
            <TabsTrigger value="list" className="gap-1.5 px-3 text-xs">
              <ListIcon className="size-3.5" />
              ລາຍຊື່ນັກສະແດງ
            </TabsTrigger>
            <TabsTrigger value="matrix" className="gap-1.5 px-3 text-xs">
              <LayoutGridIcon className="size-3.5" />
              ຕາຕະລາງ × ໂຊ
            </TabsTrigger>
          </TabsList>
        </Tabs>
        {activeTab === "matrix" ? (
          <p className="text-muted-foreground text-xs">
            {eventShows.length} ໂຊ · {assignments.length} ບັນທັດ
          </p>
        ) : null}
      </div>

      {activeTab === "list" ? listContent : matrixContent}
    </>
  );
}
